// Second-level labels that sit under a country code (acme.co.uk), so the
// parent of acme.co.uk is never treated as the company's own domain.
const SHARED_SECOND_LEVEL = new Set([
	"co",
	"com",
	"org",
	"net",
	"gov",
	"ac",
	"edu",
])

function hostOf(value: string | null | undefined): string | null {
	const host = (value ?? "")
		.trim()
		.toLowerCase()
		.replace(/^\.+|\.+$/g, "")
	return host.includes(".") ? host : null
}

// feedback.acme.com and updates.acme.com both vouch for acme.com, where the
// company usually hosts its own changelog.
function parentOf(host: string): string | null {
	const labels = host.split(".")
	if (labels.length < 3) return null
	const parent = labels.slice(1)
	const [second, tld] = parent.slice(-2)
	if (
		parent.length === 2 &&
		tld?.length === 2 &&
		SHARED_SECOND_LEVEL.has(second ?? "")
	) {
		return null
	}
	return parent.join(".")
}

interface OwnDomainSource {
	domain?: string | null
	customDomain?: string | null
	portalInstances?: readonly { customDomain?: string | null }[] | null
	emailSettings?:
		| readonly { primaryEmail: string; isEnabled?: boolean | null }[]
		| null
}

/** Domains a link may point at without the "not one of your domains" warning. */
export function changelogOwnDomains(
	workspace: OwnDomainSource | null | undefined,
): string[] {
	if (!workspace) return []

	const hosts = [
		workspace.domain ? `${workspace.domain}.productlane.com` : null,
		workspace.customDomain,
		...(workspace.portalInstances ?? []).map((portal) => portal.customDomain),
		...(workspace.emailSettings ?? [])
			.filter((address) => address.isEnabled !== false)
			.map((address) => address.primaryEmail.split("@")[1]),
	]
		.map(hostOf)
		.filter((host): host is string => !!host)

	const domains = new Set<string>()
	for (const host of hosts) {
		domains.add(host)
		if (host.endsWith(".productlane.com")) continue
		const parent = parentOf(host)
		if (parent) domains.add(parent)
	}
	return [...domains]
}
